<template>
  <div
    :id="TELIA_PAY_ROOT_DIV_ID"
    :class="{ hidden: !checkoutStore.ui.loading.isStep4TeliaPayInitiated }"
  ></div>
</template>

<script lang="ts" setup>
import type {
  TPWebCheckoutResultEvent,
  WebCheckoutEventReturnedInCallbacks,
} from '~/models/checkout/TeliaPayWebCheckoutEvent.model';

const checkoutStore = useMyCheckoutStore();
const TELIA_PAY_ROOT_DIV_ID = 'checkout__teliapay-container-el';

const emit = defineEmits(['on-reset-payment-option']);

onMounted(async () => {
  if (!import.meta.client) {
    return;
  }

  const tpRootEl = document.getElementById(TELIA_PAY_ROOT_DIV_ID);
  if (!tpRootEl) {
    return;
  }

  const {
    tpHandler,
    logEvent,
    postResult,
    tpResultOnComplete,
    tpResultOnFail,
    updateBackendSelectedDownPayment,
  } = useTeliaPay(
    tpRootEl,
    checkoutStore.teliaPayToken ?? '',
    checkoutStore.userState.payment.selectedDownPayTime ?? 12,
  );

  watch(
    tpHandler,
    (handler: any) => {
      if (!handler) {
        return;
      }

      handler.on(TeliaPayEventsToListenTo.INITIATED, function () {
        checkoutStore.ui.loading.isStep4TeliaPayInitiated = true;
      });

      handler.on(
        TeliaPayEventsToListenTo.AUTHENTICATION,
        function (webCheckoutEvent: WebCheckoutEventReturnedInCallbacks) {
          logEvent(webCheckoutEvent);
        },
      );

      handler.on(
        TeliaPayEventsToListenTo.CLICKED_CONTINUE_BUTTON,
        function (webCheckoutEvent: WebCheckoutEventReturnedInCallbacks) {
          logEvent(webCheckoutEvent);
        },
      );

      handler.on(
        TeliaPayEventsToListenTo.CLICKED_PAY_BUTTON,
        function (webCheckoutEvent: WebCheckoutEventReturnedInCallbacks) {
          logEvent(webCheckoutEvent);
        },
      );

      handler.on(
        TeliaPayEventsToListenTo.CREDIT_CHECK_DENIED,
        function (webCheckoutEvent: WebCheckoutEventReturnedInCallbacks) {
          logEvent(webCheckoutEvent);
        },
      );

      handler.on(
        TeliaPayEventsToListenTo.ERROR,
        function (webCheckoutEvent: WebCheckoutEventReturnedInCallbacks) {
          logEvent(webCheckoutEvent);
        },
      );

      handler.on(
        TeliaPayEventsToListenTo.PART_PAYMENT_SELECTED_MONTHS,
        function (webCheckoutEvent: WebCheckoutEventReturnedInCallbacks) {
          logEvent(webCheckoutEvent);

          if (webCheckoutEvent.value) {
            checkoutStore.userState.payment.initialDownpayTime =
              +`${webCheckoutEvent.value}`;
            checkoutStore.userState.payment.selectedDownPayTime =
              checkoutStore.userState.payment.initialDownpayTime;
          }

          // Don't await on purpose
          updateBackendSelectedDownPayment(webCheckoutEvent);
        },
      );

      handler.on(
        TeliaPayEventsToListenTo.PART_PAYMENT_AMOUNT_PER_MONTHS,
        function (webCheckoutEvent: WebCheckoutEventReturnedInCallbacks) {
          logEvent(webCheckoutEvent);
        },
      );

      handler.on(
        TeliaPayEventsToListenTo.PART_PAYMENT_CHOSEN_TERM,
        function (webCheckoutEvent: WebCheckoutEventReturnedInCallbacks) {
          logEvent(webCheckoutEvent);
        },
      );

      handler.on(
        TeliaPayEventsToListenTo.REDIRECT_AT_CREDIT_DENIED,
        function (webCheckoutEvent: WebCheckoutEventReturnedInCallbacks) {
          logEvent(webCheckoutEvent);
          checkoutStore.ui.loading.isStep4TeliaPayInitiated = false;
          emit('on-reset-payment-option');
        },
      );
    },
    { immediate: true },
  );

  watch(
    tpResultOnComplete,
    async (resultOnComplete: TPWebCheckoutResultEvent | null) => {
      if (!resultOnComplete) {
        return;
      }

      await postResult(resultOnComplete);

      // TODO: Lägga till hidden fält med id/name "authstatus" och value COMPLETE?

      const redirectUrl = await checkoutStore.finalizeCheckout();

      cookies_clearAccessToken();

      if (redirectUrl) {
        navigateTo(redirectUrl, { external: true });
      }
    },
  );

  watch(
    tpResultOnFail,
    async (resultOnFail: TPWebCheckoutResultEvent | null) => {
      if (!resultOnFail) {
        return;
      }
    },
  );
});

onBeforeUnmount(() => {
  checkoutStore.ui.loading.isStep4TeliaPayInitiated = false;
});
</script>
